// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Turning a stream of instantaneous motion scores into discrete events. Both the
 * live path (a state machine fed frame by frame) and the file-scan path (grouping
 * a whole score series into segments) live here, and both are pure and tested.
 */

import type { Segment } from './types';

export interface GateParams {
  /** Consecutive over-threshold frames required to start an event (debounce). */
  minActiveFrames: number;
  /** How long motion must be absent before an active event ends. */
  cooldownMs: number;
  /** Hard cap on a single event's length. */
  maxClipMs: number;
}

export type GateTransition = 'start' | 'stop' | null;

/**
 * Live motion gate. Feed it `over` (score ≥ threshold?) and a timestamp each
 * frame; it emits 'start' when sustained motion begins and 'stop' when motion has
 * settled for `cooldownMs` (or the clip hits `maxClipMs`).
 */
export class MotionGate {
  private active = false;
  private consec = 0;
  private startT = 0;
  private lastMotionT = 0;

  constructor(private readonly p: GateParams) {}

  push(over: boolean, tMs: number): GateTransition {
    if (!this.active) {
      this.consec = over ? this.consec + 1 : 0;
      if (this.consec >= this.p.minActiveFrames) {
        this.active = true;
        this.consec = 0;
        this.startT = tMs;
        this.lastMotionT = tMs;
        return 'start';
      }
      return null;
    }
    if (over) this.lastMotionT = tMs;
    const settled = tMs - this.lastMotionT >= this.p.cooldownMs;
    const tooLong = tMs - this.startT >= this.p.maxClipMs;
    if (settled || tooLong) {
      this.active = false;
      this.consec = 0;
      return 'stop';
    }
    return null;
  }

  get isActive(): boolean {
    return this.active;
  }

  reset(): void {
    this.active = false;
    this.consec = 0;
    this.startT = 0;
    this.lastMotionT = 0;
  }
}

export interface SegmentOptions {
  triggerFraction: number;
  minActiveFrames: number;
  /** Idle gap that ends a segment, in the SAME time unit as `sample.t`. */
  cooldown: number;
  /** Minimum segment length to keep, in the SAME time unit as `sample.t`. */
  minDuration: number;
}

/**
 * Group a whole score series (from scanning a video file) into motion segments,
 * each carrying the time of its strongest frame. Samples must be time-ordered.
 * All time options are in the same unit as `sample.t` (seconds, for file scans).
 */
export function segmentsFromScores(
  samples: Array<{ t: number; score: number }>,
  opts: SegmentOptions,
): Segment[] {
  const out: Segment[] = [];
  let active = false;
  let consec = 0;
  let startT = 0;
  let lastMotionT = 0;
  let peakT = 0;
  let peakScore = 0;

  const close = () => {
    if (active && lastMotionT - startT >= opts.minDuration) {
      out.push({ startT, endT: lastMotionT, peakT, peakScore });
    }
    active = false;
  };

  for (const { t, score } of samples) {
    const over = score >= opts.triggerFraction;
    if (!active) {
      consec = over ? consec + 1 : 0;
      if (consec >= opts.minActiveFrames) {
        active = true;
        consec = 0;
        startT = t;
        lastMotionT = t;
        peakT = t;
        peakScore = score;
      }
    } else {
      if (over) lastMotionT = t;
      if (score > peakScore) {
        peakScore = score;
        peakT = t;
      }
      if (t - lastMotionT >= opts.cooldown) close();
    }
  }
  close();
  return out;
}

/** mm:ss.d timecode for a number of seconds. */
export function formatTimecode(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  const whole = Math.floor(rem);
  const tenths = Math.floor((rem - whole) * 10);
  return `${String(m).padStart(2, '0')}:${String(whole).padStart(2, '0')}.${tenths}`;
}

/** ms → compact human duration like "3.4s" or "1m 05s". */
export function formatDuration(ms: number): string {
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${String(rem).padStart(2, '0')}s`;
}

/** CSV of the full motion-over-time trace. */
export function buildTimelineCSV(samples: Array<{ t: number; score: number }>): string {
  const rows = ['time_seconds,motion_score'];
  for (const s of samples) rows.push(`${s.t.toFixed(3)},${s.score.toFixed(4)}`);
  return rows.join('\n') + '\n';
}

/** JSON summary of detected segments. */
export function buildSegmentsJSON(segments: Segment[], source: string): string {
  return JSON.stringify(
    {
      tool: 'sentrywell',
      source,
      generated_note: 'Times in seconds. Motion is pixel-change based, not object detection.',
      segment_count: segments.length,
      segments: segments.map((s) => ({
        start: Number(s.startT.toFixed(3)),
        end: Number(s.endT.toFixed(3)),
        peak: Number(s.peakT.toFixed(3)),
        peak_score: Number(s.peakScore.toFixed(4)),
        duration: Number((s.endT - s.startT).toFixed(3)),
      })),
    },
    null,
    2,
  );
}
